import { CircularProgress } from "@material-ui/core";
import { Form, Formik } from "formik";
import React, { useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  ErrorUnexpected,
  OperationSuccessfully,
} from "../../../../../../const/Messages";
import * as OperatorPartnersUploadService from "../../../../../../service/OperatorPartnerUploadService";
import "./RoamingPartnersUploadUploadModal";

const initialValues = {
  file: null,
  steeringFile: null,
};

/**
 * Criação:
 * @author Guilherme Almeida
 * @date 26/06/2024
 */
export default function RoamingPartnersUploadUploadModal({ show, setShow }) {
  const [isDraggingWholesale, setIsDraggingWholesale] = useState(false);
  const [isDraggingSteering, setIsDraggingSteering] = useState(false);

  const SUPPORTED_FORMATS = [
    ".xls",
    ".xlsx",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];
  const formikRef = useRef(null);

  const isValidFileFormat = (value) => {
    if (!value) return true;
    const name = value.name ? value.name.toLowerCase() : "";
    return (
      SUPPORTED_FORMATS.includes(value.type) ||
      name.endsWith(".xls") ||
      name.endsWith(".xlsx")
    );
  };

  const schema = Yup.object()
    .shape({
      file: Yup.mixed().test(
        "fileFormat",
        "Unsupported Format. Only .xls and .xlsx are allowed.",
        isValidFileFormat
      ),
      steeringFile: Yup.mixed().test(
        "fileFormat",
        "Unsupported Format. Only .xls and .xlsx are allowed.",
        isValidFileFormat
      ),
    })
    .test(
      "at-least-one-file",
      "At least one file must be provided",
      function (values) {
        if (!values || (!values.file && !values.steeringFile)) {
          return this.createError({
            path: "file",
            message: "Select at least one file (Roaming Partners or Steering).",
          });
        }
        return true;
      }
    );

  function handleCloseModal() {
    setShow(false);
  }

  const processWholesaleFile = (file, setFieldValue) => {
    if (!file) return;

    if (!isValidFileFormat(file)) {
      toast.warning(
        `File ${file.name} has invalid format. Only .xls and .xlsx are allowed.`
      );
      return;
    }

    setFieldValue("file", file);
  };

  const handleDropWholesale = (e, setFieldValue) => {
    e.preventDefault();
    setIsDraggingWholesale(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (e.dataTransfer.files.length > 1) {
        toast.warning("Please drop only 1 file.");
        return;
      }
      processWholesaleFile(e.dataTransfer.files[0], setFieldValue);
    }
  };

  const handleFileSelectWholesale = (e, setFieldValue) => {
    if (e.target.files && e.target.files.length > 0) {
      processWholesaleFile(e.target.files[0], setFieldValue);
    }
    e.target.value = null;
  };

  const removeWholesaleFile = (setFieldValue) => {
    setFieldValue("file", null);
  };

  const processSteeringFile = (file, setFieldValue) => {
    if (!file) return;

    if (!isValidFileFormat(file)) {
      toast.warning(
        `File ${file.name} has invalid format. Only .xls and .xlsx are allowed.`
      );
      return;
    }

    setFieldValue("steeringFile", file);
  };

  const handleDropSteering = (e, setFieldValue) => {
    e.preventDefault();
    setIsDraggingSteering(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (e.dataTransfer.files.length > 1) {
        toast.warning("Please drop only 1 file.");
        return;
      }
      processSteeringFile(e.dataTransfer.files[0], setFieldValue);
    }
  };

  const handleFileSelectSteering = (e, setFieldValue) => {
    if (e.target.files && e.target.files.length > 0) {
      processSteeringFile(e.target.files[0], setFieldValue);
    }
    e.target.value = null;
  };

  const removeSteeringFile = (setFieldValue) => {
    setFieldValue("steeringFile", null);
  };

  function handleUpload(values, actions) {
    const formData = new FormData();
    if (values.file) {
      formData.append("whlFile", values.file);
    }
    if (values.steeringFile) {
      formData.append("steeringFile", values.steeringFile);
    }

    OperatorPartnersUploadService.upload(formData)
      .then(() => {
        toast.success(OperationSuccessfully);
        handleCloseModal();
      })
      .catch((error) => toast.error(ErrorUnexpected))
      .finally(() => actions.setSubmitting(false));
  }

  return (
    <Modal
      show={show}
      onHide={() => handleCloseModal(false)}
      size="xl"
      animation={false}
    >
      <Formik
        initialValues={initialValues}
        innerRef={formikRef}
        validationSchema={schema}
        onSubmit={handleUpload}
      >
        {({ values, setFieldValue, isSubmitting, errors }) => (
          <Form>
            <Modal.Header closeButton>
              <Modal.Title>Roaming Partners Upload</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <div className="row">
                {/* COLUNA WHOLESALE */}
                <div className="col-md-6 d-flex flex-column">
                  <div>
                    <h5 className="font-weight-bold">Wholesale</h5>
                    <p>
                      O arquivo Wholesale é a base de dados utilizada para os
                      acordos com os parceiros. Ao realizar o upload, as
                      informações existentes no sistema serão atualizadas de
                      acordo com o conteúdo do arquivo.
                    </p>
                    <p>
                      Atenção: essa operação é irreversível e não poderá ser
                      desfeita.
                    </p>
                    <p>
                      Durante o processamento do arquivo, as seguintes regras
                      serão aplicadas:
                    </p>
                  </div>

                  <div
                    className="overflow-auto pr-2"
                    style={{ maxHeight: "250px" }}
                  >
                    <ol>
                      <li>
                        Todos os registros com "Status do acordo" = "Vigente" e
                        "PMNCODE-TIM" = "ORIGINAL" ou "BRAPS" serão cadastrados;
                        <ul>
                          <li>
                            Exceção quando não houver o TADIG ("PMNCODE")
                            previamente cadastrado no sistema. Para este caso, o
                            sistema irá informar no alerta quais os TADIGs que
                            não foram cadastrados.
                          </li>
                          <li>
                            Quando houver "Status do acordo" "ORIGINAL" e
                            "BRAPS" para um mesmo TADIG ("PMNCODE"), o sistema
                            irá unificar as informações.
                          </li>
                        </ul>
                      </li>

                      <li>
                        Todos os TADIGs que não respeitarem a regra 1 serão
                        removidos do sistema;
                        <ul>
                          <li>
                            Exceção para os acordos cadastrados no sistema como
                            em fase de testes, estes serão mantidos, ou seja,
                            para os acordos "TESTING" ou "INBOUND OUT TESTING" =
                            "OUTBOUND IN TESTING", respeitando a regra a seguir:
                            <ul>
                              <li>
                                Se cadastro VRAT = TESTING e planilha de acordos
                                = EMPTY -&gt; mantém TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = TESTING e planilha de acordos
                                = OUT -&gt; cadastra OUTBOUND IN TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = TESTING e planilha de acordos
                                = IN -&gt; cadastra INBOUND OUT TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = TESTING e planilha de acordos
                                = IN/OUT -&gt; cadastra BILATERAL .
                              </li>
                              <li>
                                Se cadastro VRAT = OUTBOUND IN TESTING e
                                planilha de acordos = empty -&gt; cadastra
                                TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = OUTBOUND IN TESTING e
                                planilha de acordos = OUT -&gt; mantém OUTBOUND
                                IN TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = OUTBOUND IN TESTING e
                                planilha de acordos = IN -&gt; cadastra INBOUND
                                OUT TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = OUTBOUND IN TESTING e
                                planilha de acordos = IN/OUT -&gt; cadastra
                                BILATERAL.
                              </li>
                              <li>
                                Se cadastro VRAT = INBOUND OUT TESTING e
                                planilha de acordos = empty -&gt; cadastra
                                TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = INBOUND OUT TESTING e
                                planilha de acordos = OUT -&gt; cadastra
                                OUTBOUND IN TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = INBOUND OUT TESTING e
                                planilha de acordos = IN -&gt; mantém INBOUND
                                OUT TESTING.
                              </li>
                              <li>
                                Se cadastro VRAT = INBOUND OUT TESTING e
                                planilha de acordos = IN/OUT -&gt; cadastra
                                BILATERAL.
                              </li>
                            </ul>
                          </li>
                        </ul>
                      </li>
                    </ol>
                  </div>

                  {/* BLOCO FIXADO DO WHOLESALE */}
                  <div className="form-group mt-auto pt-4 mb-0">
                    <label htmlFor="file" className="font-weight-bold mb-2">
                      Wholesale File:
                    </label>
                    {!values.file ? (
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingWholesale(true);
                        }}
                        onDragLeave={() => setIsDraggingWholesale(false)}
                        onDrop={(e) => handleDropWholesale(e, setFieldValue)}
                        style={{
                          border: `2px dashed ${
                            isDraggingWholesale ? "#007bff" : "#ced4da"
                          }`,
                          backgroundColor: isDraggingWholesale
                            ? "#e9ecef"
                            : "#f8f9fa",
                          padding: "20px",
                          textAlign: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                        }}
                        onClick={() =>
                          document.getElementById("wholesaleFileInput").click()
                        }
                      >
                        <h6 className="text-muted mb-1">
                          {isDraggingWholesale
                            ? "Drop file here!"
                            : "Drag your file here or click to select"}
                        </h6>
                        <small className="text-muted">
                          Supported formats: .xls, .xlsx
                        </small>

                        <input
                          type="file"
                          id="wholesaleFileInput"
                          style={{ display: "none" }}
                          disabled={isSubmitting}
                          onChange={(e) =>
                            handleFileSelectWholesale(e, setFieldValue)
                          }
                          accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          border: "1px solid #ced4da",
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: "#fff",
                        }}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span className="text-truncate mr-2">
                          {values.file.name}
                        </span>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={isSubmitting}
                          onClick={() => removeWholesaleFile(setFieldValue)}
                          title="Remove file"
                        >
                          X
                        </Button>
                      </div>
                    )}
                    {errors.file && (
                      <div className="mt-1">
                        <span className="text-danger small">{errors.file}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* COLUNA STEERING */}
                <div className="col-md-6 d-flex flex-column mt-4 mt-md-0">
                  <div>
                    <h5 className="font-weight-bold">Steering</h5>
                    <p>
                      O arquivo Steering é utilizado para sincronizar as
                      informações de bloqueio dos acordos de roaming com os
                      parceiros. Ao realizar o upload, o sistema atualizará
                      automaticamente o status de bloqueio dos registros
                      existentes no cadastro de Roaming Partners, de acordo com
                      as informações contidas no arquivo.
                    </p>
                    <p>
                      Durante o processamento do arquivo, as seguintes regras
                      serão aplicadas:
                    </p>
                  </div>

                  <div
                    className="overflow-auto pr-2"
                    style={{ maxHeight: "250px" }}
                  >
                    <ul>
                      <li>
                        Apenas os acordos marcados como "Bloqueio Total" deverão
                        permanecer com o status de bloqueio ativo.
                      </li>
                      <li>
                        As informações da planilha serão armazenadas no sistema
                        e utilizadas como fonte de dados para todas as
                        auditorias que dependem do arquivo Steering.
                      </li>
                    </ul>
                  </div>

                  {/* BLOCO FIXADO DO STEERING */}
                  <div className="form-group mt-auto pt-4 mb-0">
                    <label
                      htmlFor="steeringFile"
                      className="font-weight-bold mb-2"
                    >
                      Steering File:
                    </label>
                    {!values.steeringFile ? (
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingSteering(true);
                        }}
                        onDragLeave={() => setIsDraggingSteering(false)}
                        onDrop={(e) => handleDropSteering(e, setFieldValue)}
                        style={{
                          border: `2px dashed ${
                            isDraggingSteering ? "#007bff" : "#ced4da"
                          }`,
                          backgroundColor: isDraggingSteering
                            ? "#e9ecef"
                            : "#f8f9fa",
                          padding: "20px",
                          textAlign: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                        }}
                        onClick={() =>
                          document.getElementById("steeringFileInput").click()
                        }
                      >
                        <h6 className="text-muted mb-1">
                          {isDraggingSteering
                            ? "Drop file here!"
                            : "Drag your file here or click to select"}
                        </h6>
                        <small className="text-muted">
                          Supported formats: .xls, .xlsx
                        </small>

                        <input
                          type="file"
                          id="steeringFileInput"
                          style={{ display: "none" }}
                          disabled={isSubmitting}
                          onChange={(e) =>
                            handleFileSelectSteering(e, setFieldValue)
                          }
                          accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          border: "1px solid #ced4da",
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: "#fff",
                        }}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span className="text-truncate mr-2">
                          {values.steeringFile.name}
                        </span>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={isSubmitting}
                          onClick={() => removeSteeringFile(setFieldValue)}
                          title="Remove file"
                        >
                          X
                        </Button>
                      </div>
                    )}
                    {errors.steeringFile && (
                      <div className="mt-1">
                        <span className="text-danger small">
                          {errors.steeringFile}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
              {isSubmitting && (
                <div className="d-flex">
                  <CircularProgress disableShrink size={16} />
                  <p className="ml-1">
                    Please wait while the file is loading. This may take a few
                    moments.
                  </p>
                </div>
              )}

              <div className="mt-3 mr-4 ml-auto d-flex justify-content-end">
                <Button
                  className="mr-1"
                  onClick={() => handleCloseModal(false)}
                  variant="primary"
                  type="button"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  Upload
                </Button>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
